import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../../api/api";
import { readPublicHero } from "../../utils/publicBootstrap";
import { imageProps, warmImage, SIZES } from "../../utils/imageUrl";
import "./HeroSlideshow.css";

const INTERVAL_MS = 8000;

// Resolves once `url` has actually loaded (near-instantly if it's already
// cached) — swapping an <img>'s src on an un-warmed URL leaves the browser
// showing the old frame until decode finishes, which is what made
// transitions look like they "fell back" to the previous slide.
const preloadImage = (url) => warmImage(url, "hero", SIZES.hero);

export default function HeroSlideshow() {
  const [slides, setSlides] = useState(() => readPublicHero() || []);
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);
  const fadeTimeoutRef = useRef(null);
  const transitionTokenRef = useRef(0);
  const mountedRef = useRef(true);
  // The URL of the slide image the browser has actually finished loading.
  const [shownUrl, setShownUrl] = useState(null);

  useEffect(() => {
    if (readPublicHero()) return;
    fetch(`${API_BASE}/hero-slides`)
      .then((r) => r.json())
      .then((d) => { if (d.slides?.length) setSlides(d.slides); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Warm the next slide in the background, so by the time auto-advance
  // needs it (INTERVAL_MS later) it is already cached — but only once the
  // CURRENT slide has finished loading. Warming it as soon as a slide became
  // active made slide 2 download alongside slide 1 on first load (~3.5 MB at
  // once on today's images), slowing the one image the visitor is waiting
  // for (speed Phase 0, 2026-10-08).
  const activeUrl = slides[active]?.image_url;
  useEffect(() => {
    if (slides.length <= 1 || !activeUrl || shownUrl !== activeUrl) return;
    const next = slides[(active + 1) % slides.length];
    if (next?.image_url) warmImage(next.image_url, "hero", SIZES.hero);
  }, [active, slides, activeUrl, shownUrl]);

  const transitionTo = (idx) => {
    const token = ++transitionTokenRef.current;
    clearTimeout(fadeTimeoutRef.current);
    preloadImage(slides[idx]?.image_url).then(() => {
      // Bail if unmounted, or a newer transition superseded this one
      // while the preload was still in flight.
      if (!mountedRef.current || transitionTokenRef.current !== token) return;
      setFading(true);
      fadeTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current || transitionTokenRef.current !== token) return;
        setActive(idx);
        setFading(false);
      }, 600);
    });
  };

  const goTo = (idx) => {
    if (idx === active) return;
    transitionTo(idx);
  };

  // Re-armed every time `active` actually settles (auto-advance or a
  // manual dot click alike), so the next auto-advance always fires
  // INTERVAL_MS after whichever slide is really showing.
  useEffect(() => {
    clearInterval(timerRef.current);
    if (slides.length <= 1) return undefined;
    timerRef.current = setInterval(() => {
      transitionTo((active + 1) % slides.length);
    }, INTERVAL_MS);
    return () => { clearInterval(timerRef.current); clearTimeout(fadeTimeoutRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, active]);

  const slide = slides[active] || slides[0];

  const isExternal = (url) => url?.startsWith("http");

  // Dots are capped at 3 regardless of how many slides exist — beyond 3,
  // show a sliding window (prev / active / next, wrapping) so every slide
  // stays reachable one click at a time instead of growing an unbounded row.
  const dotIndices = slides.length <= 3
    ? slides.map((_, i) => i)
    : [-1, 0, 1].map((offset) => (active + offset + slides.length) % slides.length);

  // Only real admin-configured slides (GET /hero-slides) ever render —
  // no hardcoded stock-photo fallback while none are active or the fetch
  // hasn't resolved yet.
  if (!slide) return null;

  return (
    <section className="hero hero-slideshow">
      {/* background image */}
      <div className={`hero-bg${fading ? " hero-bg--fading" : ""}`}>
        <img
          {...imageProps(slide.image_url, "hero", SIZES.hero)}
          alt=""
          className="hero-bg-img"
          // The first slide is the largest thing on the homepage and the
          // image Lighthouse measures as LCP: ask for it first.
          fetchPriority={active === 0 ? "high" : "auto"}
          onLoad={() => setShownUrl(slide.image_url)}
        />
      </div>

      <div className={`hero-content${fading ? " hero-content--fading" : ""}`}>
        {slide.headline && <p>{slide.headline}</p>}
        <span className="hero-tag">{slide.badge || " "}</span>
        {slide.subtext && <span className="hero-subtext">{slide.subtext}</span>}
        <div className="hero-actions">
          {slide.cta_secondary_label && slide.cta_secondary_url && (
            isExternal(slide.cta_secondary_url)
              ? <a href={slide.cta_secondary_url} className="btn-ghost">{slide.cta_secondary_label}</a>
              : <Link to={slide.cta_secondary_url} className="btn-ghost">{slide.cta_secondary_label}</Link>
          )}
        </div>
      </div>

      {/* dot navigation — only shown when there are multiple slides */}
      {slides.length > 1 && (
        <div className="hero-dots">
          {dotIndices.map((i) => (
            <button
              key={i}
              className={`hero-dot${i === active ? " hero-dot--active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {slide.cta_primary_label && slide.cta_primary_url && (
        <div className="hero-scroll-hint">
          {isExternal(slide.cta_primary_url)
            ? <a href={slide.cta_primary_url}>{slide.cta_primary_label}</a>
            : <Link to={slide.cta_primary_url}>{slide.cta_primary_label}</Link>}
          <div className="scroll-line" />
        </div>
      )}
    </section>
  );
}
